/** A blueprint in words and as a picture: its name, what it does, the hexagon it's drawn on. */
import {
  KIND,
  MACHINE_TYPES,
  MACHINES,
  MATERIALS,
  type Kind,
  type MachineType,
} from '@bramvasterd/terrella-rules';

import type { Blueprint } from '@game/build/blueprints';
import { rockFor } from '@game/planet/machines';
import { machineName, t } from '@i18n/index';
import type { HexLook } from '@ui/art/hexArt';

/** "Crystal resonator". */
export const blueprintName = (b: Blueprint): string => machineName(b.type);

/** What it does, in a sentence. */
export const blueprintWhat = (b: Blueprint): string => t(`machine.${b.type}-what`);

/**
 * The ground a machine is pictured on: an extractor's own; for a charge the softest rock no weaker
 * charge breaks (stone for the crystal charge, basalt for the heavy one); anything else on grass.
 */
export function pictureGround(type: MachineType): Kind {
  const spec = MACHINES[type];
  if (spec.role === 'extractor') return spec.ground;
  if (spec.role !== 'charge') return KIND.GRASS;
  let weaker = 0;
  for (const other of MACHINE_TYPES) {
    const s = MACHINES[other];
    if (s.role === 'charge' && s.power < spec.power) weaker = Math.max(weaker, s.power);
  }
  return (
    rockFor(spec.power).find((kind) => (MATERIALS.get(kind)?.hardness ?? 0) > weaker) ?? KIND.STONE
  );
}

/** It standing on its ground, as the hexagon art draws it. */
export const blueprintArt = (b: Blueprint): HexLook => ({
  tile: pictureGround(b.type),
  machine: b.type,
});
